import { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useIsFirstRender, useLocalStorage } from 'usehooks-ts';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  ArticleReference,
  SourceContentArea,
  useGetApiArticlesReferencesQuery,
  usePutApiSourceContentAreasMutation,
} from '../../../redux/store/api/api';
import {
  selectActiveArticleReference,
  selectActiveVersion,
} from '../../../redux/store/content/slice';
import CustomDialog from '../../dialogs/CustomDialog';
import convertFromRange from '../../version/functions/convertFromRange';
import getContentAreasFromPattern from '../../version/functions/getContentAreasFromPattern';
import { addMessage } from '../../../redux/store/layout/slice';
import makeContent from '../../version/functions/makeContent';
import testForIntersection from '../../version/functions/testForIntersection';
import {
  articleIdUrlParam,
  EDIT_MODE_KEY,
  SUGGESTION_CONTENT_CONTAINER_ID,
  SUGGESTION_CONTENT_LINK,
  SUGGESTION_ID_PREFIX,
  SUGGESTION_LINK,
} from '../../../shared/constants';
import DeleteReferenceDialog from './DeleteReferenceDialog';
import DeleteSourceContentAreaDialog from './DeleteSourceContentAreaDialog';
import makeRangeFromSuggestion from '../../version/functions/makeRangeFromSuggestion';
import '../References.scss';
import { getContentAreaFromArticleReference } from '../../version/functions';
import useGetCategoryByArticleId from '../../../hooks/useGetCategoryByArticleId';
import { RightKey } from '../../../shared/enums';
import { createReferenceTitle, getLinkIsError } from '../functions';

interface ILinkNeedsActionDialogProps {
  show: boolean;
  setShow: (show: boolean) => void;
}

function LinkNeedsActionDialog({
  show,
  setShow,
}: ILinkNeedsActionDialogProps): JSX.Element {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const version = useAppSelector(selectActiveVersion);
  const reference = useAppSelector(selectActiveArticleReference);
  const [suggestion, setSuggestion] = useState<string | null>('');

  const [selectingNewArea, setSelectingNewArea] = useState(false);
  const [deleteReferenceDialogOpened, setDeleteReferenceDialogOpened] =
    useState(false);
  const [
    deleteSourceContentAreaDialogOpened,
    setDeleteSourceContentAreaDialogOpened,
  ] = useState(false);

  const [suggestionContainer, setSuggestionContainer] = useState(
    document.getElementById(SUGGESTION_CONTENT_CONTAINER_ID),
  );
  const [showInvalidSelectionWarning, setShowInvalidSelectionWarning] =
    useState(false);
  const isFirstRender = useIsFirstRender();
  const [searchParams] = useSearchParams();
  const articleId: string | null = searchParams.get(articleIdUrlParam);
  const category = useGetCategoryByArticleId(version.articleId);
  const [editModeIsActive] = useLocalStorage<boolean>(EDIT_MODE_KEY, false);
  const linkIsError =
    getLinkIsError(reference) && !reference.sourceContentArea?.areaInvalid;

  // permissions
  const userCanEditReference =
    category?.permittedActions?.includes(
      RightKey.RightArticleManagementEditArticle,
    ) || false;
  const userCanDeleteReference = category?.permittedActions?.includes(
    RightKey.RightArticleManagementDeleteArticle,
  );

  const [
    updateSourceContentArea,
    { isError: updateSourceContentAreaIsError, error },
  ] = usePutApiSourceContentAreasMutation();
  const { data: referenceData, refetch } = useGetApiArticlesReferencesQuery(
    articleId && version.id
      ? {
          articleId,
          versionId: version.id,
        }
      : skipToken,
  );

  const closeModalResetButtons = () => {
    setShow(false);
    setSelectingNewArea(false);
    setShowInvalidSelectionWarning(false);
  };

  const makeSuggestionContent = (suggestionLink?: ArticleReference) => {
    const linksToShow =
      referenceData?.resultObject?.articleReferenceLists
        ?.flatMap((rL) => rL.articleReferences)
        .filter(
          (r) =>
            r && r.sourceContentArea?.id && !r.sourceContentArea?.areaInvalid,
        )
        .map((r) =>
          getContentAreaFromArticleReference({
            reference: r as ArticleReference,
            classNames: [SUGGESTION_CONTENT_LINK],
            idPrefix: SUGGESTION_ID_PREFIX,
          }),
        ) || [];

    if (suggestionLink) {
      linksToShow.push(
        getContentAreaFromArticleReference({
          reference: suggestionLink,
          classNames: [SUGGESTION_LINK],
          idPrefix: SUGGESTION_ID_PREFIX,
        }),
      );
    }

    return makeContent({
      content: version.htmlContent || '',
      contentAreas: linksToShow,
      isSuggestionContent: true,
      suggestionElId: suggestionLink?.sourceContentArea?.id || '',
    });
  };

  const onSave = () => {
    let range = makeRangeFromSuggestion(suggestionContainer, SUGGESTION_LINK);
    const selection = window.getSelection();

    if (!range) {
      if (selection && !selection.isCollapsed) {
        range = selection.getRangeAt(0);
      } else {
        return;
      }
    }

    if (testForIntersection(range, SUGGESTION_CONTENT_CONTAINER_ID, 'a')) {
      selection?.collapseToStart();
      setShowInvalidSelectionWarning(true);
      return;
    }

    const sourceContentAreaId = reference?.sourceContentArea?.id || '';
    const changedSorceContentArea: SourceContentArea = {
      id: sourceContentAreaId,
      ...convertFromRange(range, SUGGESTION_CONTENT_CONTAINER_ID, document),
      articleReferenceId: reference?.id || '',
    };

    updateSourceContentArea({ body: [changedSorceContentArea] })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'UpdateSourceContentAreaSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        setShow(false);
        refetch();
      });
  };

  const onSetNewLink = () => {
    if (!suggestionContainer) {
      return;
    }
    setSelectingNewArea(true);
    setSuggestion(makeSuggestionContent().content || '');
    const selection = window.getSelection();
    selection?.collapseToStart();
  };

  // Scroll to suggested link in content
  useEffect(() => {
    if (suggestionContainer && reference.sourceContentArea?.id) {
      const suggestionLink = document.querySelector(
        `[id*=${reference.sourceContentArea?.id}]`,
      );
      suggestionLink?.scrollIntoView(true);
    }
  }, [suggestionContainer, show]);

  useEffect(() => {
    if (updateSourceContentAreaIsError) {
      dispatch(
        addMessage({
          id: 'UpdateSourceVontentAreaError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [updateSourceContentAreaIsError]);

  useEffect(() => {
    if (show) {
      setSuggestionContainer(
        document.getElementById(SUGGESTION_CONTENT_CONTAINER_ID),
      );
    }
  }, [show]);

  useEffect(() => {
    if (isFirstRender || !reference || !show) {
      return;
    }

    const suggestionLink = getContentAreasFromPattern({
      tagName: 'a',
      content: makeSuggestionContent().content,
      patternContent: reference.sourceContentArea?.patternString || '',
      id: reference.sourceContentArea?.id || '',
    }) as SourceContentArea;

    if (suggestionLink) {
      setSuggestion(
        makeSuggestionContent({
          ...reference,
          sourceContentArea: {
            ...reference?.sourceContentArea,
            ...suggestionLink,
          },
        }).content,
      );
    } else {
      setSuggestion('');
    }
  }, [reference, version.htmlContent, show]);

  return (
    <>
      <CustomDialog
        titleId='LinkNeedsActionDialog'
        dialogTitle={translation('checkLink')}
        show={show}
        closeFunction={closeModalResetButtons}
        closeTitle={translation('cancel')}>
        {showInvalidSelectionWarning && (
          <Alert
            aria-label={translation('closeAlert')}
            variant='warning'
            onClose={() => setShowInvalidSelectionWarning(false)}
            dismissible>
            <p className='m-0'>{translation('makeNewSelection')}</p>
          </Alert>
        )}

        {userCanEditReference && !editModeIsActive && (
          <p>{translation('linkNotValidEditModeNeedsToBeActiveToEditMark')}</p>
        )}

        {userCanEditReference && editModeIsActive && linkIsError && (
          <p>{translation('linkIsErrorContactAdmin')}</p>
        )}

        <h5>{translation('nameOfTheReference')}:</h5>
        <p>
          {createReferenceTitle(
            reference.categoryAbbreviation,
            reference.name,
            reference.originalName,
          )}
        </p>

        {userCanEditReference &&
          editModeIsActive &&
          reference.sourceContentArea &&
          reference.sourceContentArea.areaInvalid && (
            <>
              <h5>
                {selectingNewArea
                  ? translation('selectNewArea')
                  : translation('suggestion')}
                :
              </h5>
              <div className='max-350' id={SUGGESTION_CONTENT_CONTAINER_ID}>
                {parse(suggestion || translation('noSuggestionFound'))}
              </div>
              <div className='mt-3'>
                <h5>{translation('originallyLinkedText')}:</h5>
                <p>{reference.sourceContentArea?.patternString}</p>
              </div>
            </>
          )}

        {userCanEditReference && editModeIsActive && (
          <Button
            className={`m-1 ${
              (suggestion || selectingNewArea) && !linkIsError ? '' : 'd-none'
            } optionButton`}
            variant='outline-dark'
            onClick={onSave}>
            {translation('save')}
          </Button>
        )}
        {userCanEditReference && editModeIsActive && (
          <Button
            className={`m-1 ${
              selectingNewArea || linkIsError ? 'd-none' : ''
            } option-button`}
            variant='outline-dark'
            onClick={onSetNewLink}>
            {translation('setNewLink')}
          </Button>
        )}
        {userCanDeleteReference && editModeIsActive && (
          <Button
            className='m-1 option-button'
            variant='outline-danger'
            onClick={() => {
              closeModalResetButtons();
              setDeleteSourceContentAreaDialogOpened(true);
            }}>
            {translation('deleteLink')}
          </Button>
        )}
        {userCanDeleteReference && editModeIsActive && (
          <Button
            className='m-1 option-button'
            variant='outline-danger'
            onClick={() => {
              closeModalResetButtons();
              setDeleteReferenceDialogOpened(true);
            }}>
            {translation('deleteWholeReference')}
          </Button>
        )}
      </CustomDialog>

      <DeleteSourceContentAreaDialog
        show={deleteSourceContentAreaDialogOpened}
        setShow={setDeleteSourceContentAreaDialogOpened}
      />
      <DeleteReferenceDialog
        reference={reference}
        dialogShow={deleteReferenceDialogOpened}
        setDialogShow={setDeleteReferenceDialogOpened}
      />
    </>
  );
}

export default LinkNeedsActionDialog;
